import type { Category, OutletDetails, Product } from "@/src/types/tenant";

/**
 * Direct, paged access to the product microservice for the all-products page.
 *
 * The storefront used to receive the WHOLE catalogue in one portal-api
 * `tenant/landing` response (`limit: 1000`, ~987 KB for 394 products, silent
 * truncation past 1000) and then filter/sort/render all of it in the browser.
 * These two calls replace that model:
 *
 *   getProductPage()   -> one page of products + `{ page, limit, total, pages }`
 *   getProductFacets() -> the counts the filter bar needs so its pills stay
 *                         truthful once the client only holds one page
 *
 * Both return `null` on ANY failure (missing env, non-2xx, network, bad body)
 * and never throw, so a caller can fall back to the legacy in-memory path — a
 * deploy-order mismatch (seller-web ahead of the product service) must not
 * blank the storefront.
 *
 * NEXT_PUBLIC_PRODUCT_SERVICE_URL already includes the `/api` prefix
 * (e.g. `http://localhost:3001/api`), matching tenant.service.ts.
 */
const PRODUCT_SERVICE_URL = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;

const base = () => (PRODUCT_SERVICE_URL ? PRODUCT_SERVICE_URL.replace(/\/+$/, "") : null);

export interface ProductPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ProductPageResult {
  products: Product[];
  pagination: ProductPagination;
}

export interface ProductFacets {
  total: number;
  /** Taxonomy facet buckets, one per level. */
  l1: { id: string; name: string; slug: string; count: number }[];
  l2: { id: string; name: string; slug: string; count: number }[];
  l3: { id: string; name: string; slug: string; count: number }[];
  /** Aliases of l1 / l2, kept so existing filter-bar code keeps compiling. */
  categories: { id: string; name: string; count: number }[];
  subcategories: { id: string; name: string; count: number }[];
  tags: { value: string; count: number }[];
  /** Always all four canonical bucket ids in ascending price order (count 0 when empty). */
  priceBuckets: { id: string; count: number }[];
}

export interface ProductPageQuery {
  outletId?: string;
  tenantId?: string | number;
  page?: number;
  limit?: number;
  /** Platform category id -> matched against `category._id`. */
  category?: string;
  /** Subcategory/department id (comma-separated = OR) -> matched against `subcategories._id`. */
  subcategory?: string;
  /** Canonical price-bucket ids, OR-ed: "under-300" | "300-600" | "600-1000" | "above-1000". */
  priceRanges?: string[];
  /** Exact `metadata.tags` values, OR-ed. */
  tags?: string[];
  search?: string;
  /** A real Mongo path or one of the service's aliases (`price`, `sellingPrice`, `mrp`). */
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  /**
   * Tenant merchandising order: taxonomy ids that should lead, most important
   * first. Sourced from `storefront.productPriority`. The service ranks these
   * ahead of everything else and applies `sortBy` within each group.
   */
  priority?: string[];
  /**
   * Ask the service to collapse a seller's copies of one product across their
   * branches to a single row, so a two-branch store shows one card per real
   * product instead of one per outlet document.
   *
   * `pagination.total` follows the same rule, which is what makes the count
   * line above the grid agree with the cards under it. A service build that
   * predates the flag ignores it and returns the raw union — harmless, because
   * the caller still groups the page it receives.
   */
  unique?: boolean;
}

export type ProductFacetQuery = Pick<
  ProductPageQuery,
  "outletId" | "tenantId" | "category" | "subcategory" | "search" | "tags" | "priceRanges" | "unique"
>;

/**
 * Shape the microservice actually returns. It is NOT the storefront `Product`
 * shape — tags live under `metadata.tags`, `featured` under `status.featured`,
 * and `variants` are full variant documents. `normalizeProduct` below closes
 * that gap so both data paths hand the UI identical objects.
 */
interface RawVariant {
  _id?: string;
  sku?: string;
  variant?: {
    size?: string;
    color?: string | { name?: string };
    weight?: string;
    volume?: string;
  };
  attributes?: { label?: string; value?: string }[];
  images?: { thumbnail?: string };
  pricing?: { sellingPrice?: number | string; mrp?: number | string };
  inventory?: { quantity?: number };
}

interface RawProduct {
  _id: string;
  sku?: string;
  name: string;
  tenantId?: string;
  category?: Category;
  subcategories?: Category[];
  outletId?: string;
  brandId?: string;
  description?: string;
  tags?: string[];
  metadata?: { tags?: string[] };
  status?: { featured?: boolean };
  inventory?: Product["inventory"];
  pricing?: Product["pricing"];
  images?: Product["images"];
  variants?: RawVariant[];
  outlet?: OutletDetails;
}

/**
 * Human label for a variant, e.g. "EU 26" or "L, Blue".
 * Byte-for-byte the same rule portal-api's landing enrichment uses, so a card's
 * variant picker reads the same whichever path fed it.
 */
function buildVariantLabel(v: RawVariant): string {
  const parts: string[] = [];
  if (v.variant?.size) parts.push(String(v.variant.size));
  if (v.variant?.color) {
    const c = typeof v.variant.color === "object" ? v.variant.color?.name : v.variant.color;
    if (c && String(c).toLowerCase() !== "default") parts.push(String(c));
  }
  if (v.variant?.weight) parts.push(String(v.variant.weight));
  if (v.variant?.volume) parts.push(String(v.variant.volume));
  if (parts.length === 0 && Array.isArray(v.attributes)) {
    return v.attributes
      .map((a) => a?.value)
      .filter(Boolean)
      .join(", ");
  }
  return parts.join(", ");
}

/**
 * Project a raw microservice product onto the storefront `Product` type, using
 * exactly the same field set and derivations as portal-api's landing enrichment
 * (TenantController `enrichedProducts`). Deliberately a projection, not a
 * spread: it drops `presentation` / `editorial` / `attributes` / `metadata`,
 * which the listing never reads.
 */
function normalizeProduct(raw: RawProduct): Product {
  const variants = Array.isArray(raw.variants) ? raw.variants : [];
  return {
    _id: raw._id,
    sku: raw.sku,
    name: raw.name,
    tenantId: raw.tenantId,
    category: raw.category,
    subcategories: raw.subcategories,
    outletId: raw.outletId,
    brandId: raw.brandId,
    description: raw.description,
    tags: raw.tags ?? raw.metadata?.tags ?? [],
    featured: raw.status?.featured ?? false,
    inventory: raw.inventory,
    pricing: raw.pricing,
    images: raw.images,
    variantCount: variants.length,
    variants: variants.map((v) => ({
      variantId: String(v._id),
      sku: v.sku ?? "",
      label: buildVariantLabel(v) || v.sku || "",
      thumbnail: v.images?.thumbnail,
      sellingPrice:
        v.pricing?.sellingPrice != null ? String(Number(v.pricing.sellingPrice).toFixed(2)) : undefined,
      mrp: v.pricing?.mrp != null ? String(Number(v.pricing.mrp).toFixed(2)) : undefined,
      stockQuantity: v.inventory?.quantity ?? 0,
    })),
    outlet: raw.outlet,
  };
}

/** Shared query-string builder so the page and facet calls can never disagree on scoping. */
function buildParams(q: ProductPageQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.outletId) params.set("outletId", q.outletId);
  if (q.tenantId != null && q.tenantId !== "") params.set("tenantId", String(q.tenantId));
  if (q.category) params.set("category", q.category);
  if (q.subcategory) params.set("subcategory", q.subcategory);
  if (q.priceRanges?.length) params.set("priceRanges", q.priceRanges.join(","));
  if (q.tags?.length) params.set("tags", q.tags.join(","));
  if (q.search) params.set("search", q.search);
  // Tenant merchandising order; the service ranks these ids first and applies
  // the requested sort within each group.
  if (q.priority?.length) params.set("priority", q.priority.join(","));
  // Opt-in on the service, because the same endpoint serves the seller CRM,
  // where a seller edits stock and price per branch and needs one row per outlet.
  if (q.unique) params.set("unique", "true");
  return params;
}

/**
 * One page of products for an outlet/tenant. `null` means "the paged endpoint
 * is unusable right now" — fall back to whatever the caller already has.
 */
export async function getProductPage(q: ProductPageQuery): Promise<ProductPageResult | null> {
  const root = base();
  if (!root) return null;
  if (!q.outletId && !q.tenantId) return null;

  try {
    const params = buildParams(q);
    params.set("page", String(Math.max(1, q.page ?? 1)));
    params.set("limit", String(Math.max(1, q.limit ?? 24)));
    if (q.sortBy) {
      params.set("sortBy", q.sortBy);
      params.set("sortOrder", q.sortOrder ?? "asc");
    }

    const res = await fetch(`${root}/products?${params.toString()}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;

    const body = await res.json();
    const data = body?.data;
    if (!data || !Array.isArray(data.products)) return null;

    const p = data.pagination ?? {};
    const limit = Number(p.limit) || Number(q.limit) || 24;
    const total = Number(p.total) || 0;
    return {
      products: (data.products as RawProduct[]).map(normalizeProduct),
      pagination: {
        page: Number(p.page) || 1,
        limit,
        total,
        pages: Number(p.pages) || (limit > 0 ? Math.ceil(total / limit) : 0),
      },
    };
  } catch (err) {
    console.error("getProductPage error:", err);
    return null;
  }
}

/**
 * Filter-bar counts for the same scope, in one aggregation.
 *
 * Every param you pass narrows EVERY count, so pass only the dimensions that
 * should constrain the pills. Callers wanting stable category/price pills while
 * a category or price bucket is selected must omit those params.
 */
export async function getProductFacets(q: ProductFacetQuery): Promise<ProductFacets | null> {
  const root = base();
  if (!root) return null;
  if (!q.outletId && !q.tenantId) return null;

  try {
    const params = buildParams(q);
    const res = await fetch(`${root}/products/facets?${params.toString()}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;

    const body = await res.json();
    const data = body?.data;
    // A facets payload always carries all four price buckets; anything else means
    // we're talking to a build that predates the endpoint.
    if (!data || !Array.isArray(data.priceBuckets)) return null;

    return {
      total: Number(data.total) || 0,
      l1: Array.isArray(data.l1) ? data.l1 : [],
      l2: Array.isArray(data.l2) ? data.l2 : [],
      l3: Array.isArray(data.l3) ? data.l3 : [],
      categories: Array.isArray(data.categories) ? data.categories : [],
      subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
      tags: Array.isArray(data.tags) ? data.tags : [],
      priceBuckets: data.priceBuckets,
    };
  } catch (err) {
    console.error("getProductFacets error:", err);
    return null;
  }
}
